import React, { useCallback } from "react";
import { Button, Modal } from "semantic-ui-react";
import {
  AQUA_BLUE,
  BLACK,
  DARK_BLUE,
  IGNIS_RED,
  TERRA_GREEN,
  WHITE,
} from "@120wateraudit/waterworks";
import styled from "styled-components";
import { toastError } from "src/utils/toast";
import { useUpdateSubmissionStatusesMutation } from "src/services/state-submission";
import { SubmissionCommunicationStatus } from "src/types/enums/SubmissionCommunicationStatus";
import Loader from "src/components/Loader";
import { SubmissionStatus } from "src/types/Submission";

interface Props {
  value: string;
  pwsId: number;
  submissionPeriodId: number;
  pwsName: string;
  submissionCommunicationId: number;
  submissionType?: string;
  refetch: () => void;
  closeModal: () => void;
}

const getConsumerNoticeVerificationText = (
  status: SubmissionCommunicationStatus
): JSX.Element => {
  switch (status) {
    default:
      return <h4>Are you sure you want to change the status?</h4>;
  }
};

export const getColorFromCommunicationStatusModal = (
  status: SubmissionStatus
): string => {
  switch (status) {
    case SubmissionStatus.submitted:
      return DARK_BLUE;
    case SubmissionStatus.approved:
      return TERRA_GREEN;
    case SubmissionStatus.rejected:
      return IGNIS_RED;
    case SubmissionStatus.inReview:
      return DARK_BLUE;
    case SubmissionStatus.voided:
      return IGNIS_RED;
    default:
      return BLACK;
  }
};

const ChangeSubmissionStatusModal = ({
  value,
  pwsName,
  submissionCommunicationId,
  submissionType = "Customer Notice",
  closeModal,
}: Props): JSX.Element => {
  const submissionLabel = submissionType
    ? `${submissionType} Submission Status`
    : "Submission Status";

  const [updateSubmissionStatuses, { isLoading }] =
    useUpdateSubmissionStatusesMutation();

  const onSubmit = useCallback(async () => {
    try {
      await updateSubmissionStatuses([
        { id: submissionCommunicationId, status: value },
      ]).unwrap();
    } catch {
      toastError("An error occurred, please try again.");
    }
  }, [updateSubmissionStatuses]);

  return (
    <ChangeStatusContainer>
      <StyledModalContent>
        <h3>
          {`You are about to change PWS ${pwsName || ""} ${submissionLabel} to `}
          <ValueContainer
            color={getColorFromCommunicationStatusModal(
              SubmissionStatus[value as keyof typeof SubmissionStatus]
            )}
          >
            <span
              style={{
                color:
                  value === SubmissionCommunicationStatus.rejected
                    ? "black"
                    : getColorFromCommunicationStatusModal(
                        SubmissionStatus[value as keyof typeof SubmissionStatus]
                      ),
              }}
            >
              {SubmissionStatus[value as keyof typeof SubmissionStatus]}
            </span>
          </ValueContainer>
        </h3>

        <Modal.Description>
          {getConsumerNoticeVerificationText(
            value as SubmissionCommunicationStatus
          )}
        </Modal.Description>
      </StyledModalContent>
      <StyledModalActions>
        {isLoading ? (
          <Loader />
        ) : (
          <Button
            onClick={() => {
              void onSubmit();
              closeModal();
            }}
            variant="primary"
            style={{
              backgroundColor: AQUA_BLUE,
              color: WHITE,
            }}
          >
            Update Submission Status
          </Button>
        )}
      </StyledModalActions>
    </ChangeStatusContainer>
  );
};

const ChangeStatusContainer = styled.div`
  margin: 0 0 40px 0;
`;

const StyledModalContent = styled(Modal.Content)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  h3 {
    max-width: 555px;
  }
`;

const StyledModalActions = styled(Modal.Actions)`
  margin: 20px 0;
  text-align: center;
`;

const ValueContainer = styled.span<{ color: string }>`
  color: ${({ color }) => color};
`;

export default ChangeSubmissionStatusModal;
